"use client";

import { MessageSquare, Shield, Crown, Circle, Clock, Mail, Building2, DollarSign } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { getInitials, statusColor, statusLabel, formatTime } from "./helpers";
import type { ChannelMember } from "./TeamChatService";

export interface AgentProfileCardProps {
  member: ChannelMember;
  presenceStatus?: { status: string; lastSeen?: string };
  onDm: () => void;
}

export function AgentProfileCard({
  member,
  presenceStatus,
  onDm,
}: AgentProfileCardProps) {
  const isOnline = presenceStatus?.status === "online" || presenceStatus?.status === "available" ||
    member.user.isOnline || ["available", "online"].includes(member.user.userStatus || "");

  const currentStatus = presenceStatus?.status || member.user.userStatus || (isOnline ? "online" : "offline");
  const brandBg = 'var(--primary, #3b82f6)';

  return (
    <div className="w-72 overflow-hidden rounded-xl shadow-2xl">
      {/* Banner */}
      <div className="h-16 relative" style={{ background: brandBg }}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
      </div>

      {/* Avatar overlapping banner */}
      <div className="bg-white dark:bg-zinc-800 px-4 pt-0 pb-4 -mt-8 relative">
        <div className="relative inline-block mb-3">
          <Avatar className="w-16 h-16 border-4 border-white dark:border-zinc-800 shadow-lg">
            <AvatarImage src={member.user.profileImageUrl} />
            <AvatarFallback className="text-lg font-bold bg-blue-600 text-white">
              {getInitials(member.user.name || member.user.email)}
            </AvatarFallback>
          </Avatar>
          <span className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-[3px] border-white dark:border-zinc-800 ${statusColor(currentStatus, isOnline)}`} />
        </div>

        {/* Name + role badges */}
        <div className="mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-100">
              {member.user.name || member.user.email}
            </h3>
            {member.leaderboardRank && member.leaderboardRank <= 3 && (
              <span className="text-lg leading-none">
                {member.leaderboardRank === 1 ? "\uD83E\uDD47" : member.leaderboardRank === 2 ? "\uD83E\uDD48" : "\uD83E\uDD49"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {member.agencyRole === "owner" && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center gap-1">
                <Crown className="w-2.5 h-2.5" /> Owner
              </span>
            )}
            {member.agencyRole === "admin" && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 flex items-center gap-1">
                <Shield className="w-2.5 h-2.5" /> Admin
              </span>
            )}
            {member.agencyTitle && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300">
                {member.agencyTitle}
              </span>
            )}
          </div>
        </div>

        {/* Agency + Premium */}
        {member.agencyName && (
          <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400 mb-2">
            <span className="w-3.5 flex items-center justify-center flex-shrink-0"><Building2 className="w-3 h-3" /></span>
            <span>{member.agencyName}</span>
          </div>
        )}
        {member.totalPremium != null && member.totalPremium > 0 && (
          <div className="flex items-center text-xs text-emerald-600 dark:text-emerald-400 mb-2">
            <DollarSign className="w-3 h-3 flex-shrink-0 -mr-0.5" />
            <span className="font-semibold">{member.totalPremium.toLocaleString()} Premium Sold This Month</span>
          </div>
        )}

        {/* Divider */}
        <div className="h-px bg-zinc-100 dark:bg-zinc-700 mb-3" />

        {/* Status info */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2 text-xs">
            <Circle className={`w-3 h-3 ${statusColor(currentStatus, isOnline)} rounded-full`} />
            <span className="font-medium text-zinc-700 dark:text-zinc-300">{statusLabel(currentStatus, isOnline)}</span>
          </div>
          {!isOnline && presenceStatus?.lastSeen && (
            <div className="flex items-center gap-2 text-xs text-zinc-400">
              <Clock className="w-3 h-3" />
              <span>Last seen {formatTime(presenceStatus.lastSeen)}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Mail className="w-3 h-3" />
            <span className="truncate">{member.user.email}</span>
          </div>
        </div>

        {/* Action buttons */}
        <button
          onClick={onDm}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors hover:opacity-90"
          style={{ background: brandBg }}
        >
          <MessageSquare className="w-4 h-4" />
          Message
        </button>
      </div>
    </div>
  );
}
